/**
 * Classifies SQL errors as AI-fixable (syntax, logic) vs. unfixable (infrastructure, permissions).
 *
 * Single Responsibility: owns only error classification rules.
 * Open/Closed: new patterns are added to UNFIXABLE_PATTERNS without changing the classifier function.
 */

export interface ErrorClassification {
  fixable: boolean;
  reason?: string;
}

interface UnfixablePattern {
  pattern: RegExp;
  reason: string;
}

/**
 * Infrastructure / environment errors that cannot be resolved by rewriting SQL.
 * Each entry maps a regex (tested against the full error text, case-insensitive)
 * to a user-facing explanation.
 */
const UNFIXABLE_PATTERNS: UnfixablePattern[] = [
  {
    pattern: /ORA-009(42|80)|TABLE OR VIEW .* DOES NOT EXIST|RELATION .* DOES NOT EXIST/,
    reason: 'The referenced table or view does not exist in this database. Verify the object name and schema, or contact a DBA.',
  },
  {
    pattern: /ORA-12\d{3}|TNS|CONNECTION.*(REFUSED|RESET|TIMED?\s*OUT|FAILED|LOST)|NETWORK\s*ADAPTER|SOCKET|ECONNREFUSED|EHOSTUNREACH/,
    reason: 'Database connectivity failed. Check that the database is reachable and credentials are correct.',
  },
  {
    pattern: /ORA-01017|INVALID USERNAME|LOGIN DENIED|AUTHENTICATION FAIL/,
    reason: 'Authentication failed. Verify database credentials.',
  },
  {
    pattern: /ORA-01031|INSUFFICIENT PRIVILEGES|PERMISSION DENIED|ACCESS DENIED/,
    reason: 'Insufficient privileges. The database user does not have access to the required objects.',
  },
  {
    pattern: /ORA-01653|ORA-01654|ORA-01652|TABLESPACE.*FULL|OUT OF (SPACE|MEMORY|TEMP)/,
    reason: 'Database resource exhaustion (tablespace/memory). Contact a DBA.',
  },
  {
    pattern: /ORA-04031|SHARED POOL|PGA.*LIMIT/,
    reason: 'Database shared memory limit reached. Contact a DBA.',
  },
  {
    pattern: /DATABASE.*UNAVAILABLE|DATABASE.*(DOWN|SHUTDOWN)|MOUNTED.*NOT.*OPEN/,
    reason: 'The database is currently unavailable. Try again later or contact a DBA.',
  },
];

/**
 * Classify a combined error string to determine if an AI auto-fix can help.
 *
 * @param errorText - The concatenated error details (from validation + test results).
 * @returns `{ fixable: true }` for SQL-rewritable errors, or `{ fixable: false, reason }` otherwise.
 */
export function classifySqlError(errorText: string): ErrorClassification {
  const upper = errorText.toUpperCase();
  for (const { pattern, reason } of UNFIXABLE_PATTERNS) {
    if (pattern.test(upper)) {
      return { fixable: false, reason };
    }
  }
  return { fixable: true };
}
